import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { createApiClient } from '../../../api/apiClient';
import { RootState, useAppDispatch } from '../../../store';
import {
  AdminLocation,
  setAdminLocations,
  setAdminLocationsError,
  setAdminLocationsLoading,
} from '../../../store/slices/adminLocationsSlice';

/** Cache duration: 5 minutes */
const CACHE_TTL_MS = 5 * 60 * 1000;
/** Default page size used by screens that don't override pagination size. */
const DEFAULT_ADMIN_LOCATIONS_PAGE_SIZE = 100;

interface UseAdminLocationsOptions {
  pageSize?: number;
}

/**
 * Hook to fetch active locations from /api/admin/locations?status=Active.
 * Results are cached in Redux and only re-fetched after CACHE_TTL_MS expires.
 *
 * Returns the locations array, loading state, error, and a manual refetch function.
 */
export function useAdminLocations(options: UseAdminLocationsOptions = {}) {
  const dispatch = useAppDispatch();
  const { locations, loading, error, fetchedAt } = useSelector(
    (state: RootState) => state.adminLocations,
  );
  const pageSize = options.pageSize ?? DEFAULT_ADMIN_LOCATIONS_PAGE_SIZE;

  const fetchLocations = useCallback(async () => {
    try {
      dispatch(setAdminLocationsLoading());
      const api = createApiClient();

      // The API paginates results. Request the configured page size and still
      // iterate pages to guarantee complete results.
      let allLocations: AdminLocation[] = [];
      let page = 0;
      let hasNext = true;

      while (hasNext) {
        const response = await api.get(`/admin/locations?status=Active&pageSize=${pageSize}&page=${page}`);
        // axios wraps in response.data; API body has { data: [...], hasNext, ... }
        const body = response?.data;
        const pageData: AdminLocation[] = body?.data ?? [];
        allLocations = [...allLocations, ...pageData];
        hasNext = body?.hasNext ?? false;
        page++;
      }

      dispatch(setAdminLocations(allLocations));
    } catch (err: any) {
      const message = err?.message || 'Failed to fetch admin locations';
      console.error('[useAdminLocations] Error:', message);
      dispatch(setAdminLocationsError(message));
    }
  }, [dispatch, pageSize]);

  useEffect(() => {
    // Only fetch if cache is stale or empty
    const isCacheValid = fetchedAt && Date.now() - fetchedAt < CACHE_TTL_MS;
    if (!isCacheValid && !loading) {
      fetchLocations();
    }
  }, [fetchLocations, fetchedAt, loading]);

  return {
    locations,
    loading,
    error,
    fetchedAt,
    refetch: fetchLocations,
  };
}
